import { useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import OtpInput from "./OtpInput";
import { requestPatientAccessOtp, verifyPatientAccessOtp } from "../api/professionalPatient";
import { declencherAccesUrgence } from "../api/liaisonParentEnfant";
import { demanderAccesViaContactSurete, verifierAccesViaContactSurete } from "../api/contactSurete";
import { getIdentityVerificationPath } from "../lib/identityVerification";

export type ContactSureteDisponible = { id: string; nom: string; priorite: number };

interface PatientAccessOtpModalProps {
  open: boolean;
  patientNip: string;
  patientDisplayName?: string;
  /** Patient mineur (système Parent-Enfant) : le flux OTP classique est impossible (pas d'email
   * propre à l'enfant) — propose un accès d'urgence tracé à la place. */
  estMineur?: boolean;
  /** Contacts de sûreté confirmés, triés par priorité (patient adulte uniquement). */
  contactsSureteDisponibles?: ContactSureteDisponible[];
  /** Bypass d'urgence générique — disponible pour tout patient (mineur ou adulte sans contact). */
  accesUrgenceDisponible?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = "prompt" | "otp";
/** Quel canal a été choisi pour l'étape OTP en cours. */
type Canal = "patient" | "contact";

export default function PatientAccessOtpModal({
  open,
  patientNip,
  patientDisplayName,
  estMineur = false,
  contactsSureteDisponibles = [],
  accesUrgenceDisponible = false,
  onClose,
  onSuccess,
}: PatientAccessOtpModalProps) {
  const [step, setStep] = useState<Step>("prompt");
  const [canal, setCanal] = useState<Canal>("patient");
  const [mode, setMode] = useState<"patient" | "contact" | "urgence">(estMineur ? "urgence" : "patient");
  const [selectedContactId, setSelectedContactId] = useState(contactsSureteDisponibles[0]?.id ?? "");
  const [justification, setJustification] = useState("");
  const [otpRequestId, setOtpRequestId] = useState("");
  const [userHint, setUserHint] = useState("");
  const [devOtpHint, setDevOtpHint] = useState("");
  const [otp, setOtp] = useState("");
  const [motifUrgence, setMotifUrgence] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callerNotVerified, setCallerNotVerified] = useState(false);
  const navigate = useNavigate();
  const identityVerificationPath = getIdentityVerificationPath(window.location.pathname);

  if (!open) return null;

  const reset = () => {
    setStep("prompt");
    setMode(estMineur ? "urgence" : "patient");
    setSelectedContactId(contactsSureteDisponibles[0]?.id ?? "");
    setJustification("");
    setOtpRequestId("");
    setUserHint("");
    setDevOtpHint("");
    setOtp("");
    setMotifUrgence("");
    setError(null);
    setCallerNotVerified(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError(null);
    setCallerNotVerified(false);
    const out = await requestPatientAccessOtp(patientNip);
    setLoading(false);
    if (!out.ok) {
      if (out.errorCode === "PATIENT_EMAIL_MANQUANT") {
        setError("Ce patient n'a pas d'e-mail enregistré, contactez un administrateur.");
      } else if (out.errorCode === "LIAISON_DEJA_ACTIVE") {
        onSuccess();
        return;
      } else if (out.errorCode === "IDENTITE_NON_VERIFIEE") {
        setError(out.message || "Votre compte doit être vérifié avant de pouvoir effectuer cette action.");
        setCallerNotVerified(true);
      } else {
        setError(out.message || "Impossible d'envoyer le code au patient.");
      }
      return;
    }
    setCanal("patient");
    setOtpRequestId(out.data.otpRequestId);
    setUserHint(out.data.userHint);
    setStep("otp");
  };

  const handleSendCodeToContact = async () => {
    if (!selectedContactId || !justification.trim()) return;
    setLoading(true);
    setError(null);
    setCallerNotVerified(false);
    const out = await demanderAccesViaContactSurete(patientNip, selectedContactId, justification.trim());
    setLoading(false);
    if (!out.ok) {
      if (out.errorCode === "IDENTITE_NON_VERIFIEE") {
        setError(out.message || "Votre compte doit être vérifié avant de pouvoir effectuer cette action.");
        setCallerNotVerified(true);
      } else {
        setError(out.message || "Impossible d'envoyer le code au contact de sûreté.");
      }
      return;
    }
    setCanal("contact");
    setOtpRequestId(out.data.otpRequestId);
    setUserHint(out.data.contactNomMasque);
    setDevOtpHint(out.data.devOtpCode ?? "");
    setStep("otp");
  };

  const handleUrgence = async () => {
    const motif = motifUrgence.trim();
    if (!motif) {
      setError("Le motif de l'accès d'urgence est requis.");
      return;
    }
    setLoading(true);
    setError(null);
    const out = await declencherAccesUrgence(patientNip, motif);
    setLoading(false);
    if (!out.ok) {
      if (out.errorCode === "IDENTITE_NON_VERIFIEE") {
        setError(out.message || "Votre compte doit être vérifié avant de pouvoir effectuer cette action.");
        setCallerNotVerified(true);
      } else {
        setError(out.message || "Impossible de déclencher l'accès d'urgence.");
      }
      return;
    }
    reset();
    onSuccess();
  };

  const handleVerify = async () => {
    if (otp.length !== 6) return;
    setLoading(true);
    setError(null);
    const out =
      canal === "contact"
        ? await verifierAccesViaContactSurete(patientNip, otpRequestId, otp)
        : await verifyPatientAccessOtp(patientNip, otpRequestId, otp);
    setLoading(false);
    if (!out.ok) {
      setError(
        out.errorCode === "OTP_INVALIDE"
          ? "Code invalide ou expiré, réessayez ou renvoyez un nouveau code."
          : out.message || "Impossible de valider le code."
      );
      return;
    }
    reset();
    onSuccess();
  };

  const handleResend = () => {
    if (canal === "contact") void handleSendCodeToContact();
    else void handleSendCode();
  };

  const title = estMineur
    ? "Accès d'urgence — patient mineur"
    : mode === "urgence"
      ? "Accès d'urgence"
      : "Accès au dossier requis";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-access-otp-title"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-md rounded-[12px] bg-white p-6 shadow-xl text-[#0B1F16]"
        style={{ border: "1px solid rgba(0,0,0,0.08)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="modal-access-otp-title" className="text-xl font-bold">
            {title}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-[6px] text-[#0B1F16]/70 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {step === "prompt" && !estMineur && (
          <>
            <div className="flex gap-2 mb-4 border-b" style={{ borderColor: "#E5E7EB" }}>
              <button
                type="button"
                onClick={() => setMode("patient")}
                className={`px-3 py-2 text-sm font-medium border-b-2 ${mode === "patient" ? "border-[#00553E] text-[#00553E]" : "border-transparent text-gray-500"}`}
              >
                Code au patient
              </button>
              {contactsSureteDisponibles.length > 0 && (
                <button
                  type="button"
                  onClick={() => setMode("contact")}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${mode === "contact" ? "border-[#00553E] text-[#00553E]" : "border-transparent text-gray-500"}`}
                >
                  Contact de sûreté
                </button>
              )}
              {accesUrgenceDisponible && (
                <button
                  type="button"
                  onClick={() => setMode("urgence")}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${mode === "urgence" ? "border-[#B45309] text-[#B45309]" : "border-transparent text-gray-500"}`}
                >
                  Urgence
                </button>
              )}
            </div>

            {mode === "patient" && (
              <>
                <p className="text-sm text-[#0B1F16]/80 mb-6">
                  Vous n'avez pas encore consulté{patientDisplayName ? ` ${patientDisplayName}` : " ce patient"}. Un
                  code d'autorisation doit être transmis par le patient pour continuer.
                </p>
                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                <div className="flex items-center justify-between gap-2">
                  {callerNotVerified && identityVerificationPath ? (
                    <button
                      type="button"
                      onClick={() => {
                        handleClose();
                        navigate(identityVerificationPath);
                      }}
                      className="text-sm font-medium text-[#00553E] hover:underline"
                    >
                      Vérifier mon identité
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={loading || callerNotVerified}
                    className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "#00553E" }}
                  >
                    {loading ? "Envoi..." : "Envoyer le code au patient"}
                  </button>
                </div>
              </>
            )}

            {mode === "contact" && (
              <>
                <p className="text-sm text-[#0B1F16]/80 mb-4">
                  {patientDisplayName || "Ce patient"} est injoignable : vous pouvez solliciter l'un de ses contacts
                  de sûreté confirmés, par ordre de priorité.
                </p>
                <div className="space-y-2 mb-4">
                  {contactsSureteDisponibles.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 rounded-[8px] border px-3 py-2 text-sm cursor-pointer"
                      style={{ borderColor: selectedContactId === c.id ? "#00553E" : "#E5E7EB" }}
                    >
                      <input
                        type="radio"
                        name="contact-surete"
                        checked={selectedContactId === c.id}
                        onChange={() => setSelectedContactId(c.id)}
                      />
                      <span>
                        Priorité {c.priorite} — {c.nom}
                      </span>
                    </label>
                  ))}
                </div>
                <label htmlFor="justification-contact" className="block text-sm font-medium text-[#0B1F16] mb-2">
                  Justification (obligatoire)
                </label>
                <textarea
                  id="justification-contact"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                  placeholder="Ex. Patient inconscient, contact prévenu par téléphone"
                />
                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                <div className="flex items-center justify-between gap-2">
                  {callerNotVerified && identityVerificationPath ? (
                    <button
                      type="button"
                      onClick={() => {
                        handleClose();
                        navigate(identityVerificationPath);
                      }}
                      className="text-sm font-medium text-[#00553E] hover:underline"
                    >
                      Vérifier mon identité
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={handleSendCodeToContact}
                    disabled={loading || callerNotVerified || !selectedContactId || !justification.trim()}
                    className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "#00553E" }}
                  >
                    {loading ? "Envoi..." : "Envoyer le code au contact"}
                  </button>
                </div>
              </>
            )}

            {mode === "urgence" && (
              <>
                <p className="text-sm text-[#0B1F16]/80 mb-4">
                  {patientDisplayName || "Ce patient"} est injoignable et aucun contact de sûreté n'est
                  disponible : vous pouvez déclencher un accès d'urgence — action tracée et notifiée au patient.
                </p>
                <label htmlFor="motif-urgence" className="block text-sm font-medium text-[#0B1F16] mb-2">
                  Motif de l'accès d'urgence
                </label>
                <textarea
                  id="motif-urgence"
                  value={motifUrgence}
                  onChange={(e) => setMotifUrgence(e.target.value)}
                  rows={3}
                  className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
                  placeholder="Ex. Patient amené en urgence, injoignable, aucun contact ne répond"
                />
                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                <div className="flex items-center justify-between gap-2">
                  {callerNotVerified && identityVerificationPath ? (
                    <button
                      type="button"
                      onClick={() => {
                        handleClose();
                        navigate(identityVerificationPath);
                      }}
                      className="text-sm font-medium text-[#00553E] hover:underline"
                    >
                      Vérifier mon identité
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={handleUrgence}
                    disabled={loading || callerNotVerified || !motifUrgence.trim()}
                    className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                    style={{ background: "#B45309" }}
                  >
                    {loading ? "Envoi..." : "Déclencher l'accès d'urgence"}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {step === "prompt" && estMineur && (
          <>
            <p className="text-sm text-[#0B1F16]/80 mb-4">
              {patientDisplayName ? `${patientDisplayName} est` : "Ce patient est"} mineur : seul son parent (via son
              propre compte) peut normalement agir sur son dossier. Si le parent est injoignable, vous pouvez
              déclencher un accès d'urgence — action tracée et notifiée au parent.
            </p>
            <label htmlFor="motif-urgence-mineur" className="block text-sm font-medium text-[#0B1F16] mb-2">
              Motif de l'accès d'urgence
            </label>
            <textarea
              id="motif-urgence-mineur"
              value={motifUrgence}
              onChange={(e) => setMotifUrgence(e.target.value)}
              rows={3}
              className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              placeholder="Ex. Enfant amené en urgence, parent injoignable au téléphone"
            />
            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
            <div className="flex items-center justify-between gap-2">
              {callerNotVerified && identityVerificationPath ? (
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    navigate(identityVerificationPath);
                  }}
                  className="text-sm font-medium text-[#00553E] hover:underline"
                >
                  Vérifier mon identité
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={handleUrgence}
                disabled={loading || callerNotVerified || !motifUrgence.trim()}
                className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                style={{ background: "#B45309" }}
              >
                {loading ? "Envoi..." : "Déclencher l'accès d'urgence"}
              </button>
            </div>
          </>
        )}

        {step === "otp" && (
          <>
            <p className="text-sm text-[#0B1F16]/80 mb-4">
              Code envoyé à <strong>{userHint}</strong>. Demandez-le{" "}
              {canal === "contact" ? "au contact de sûreté" : "au patient"} et saisissez-le ci-dessous.
            </p>
            {devOtpHint && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-[6px] px-2 py-1 mb-4">
                Dev uniquement — code : <strong>{devOtpHint}</strong>
              </p>
            )}
            <div className="mb-4">
              <OtpInput length={6} value={otp} onChange={setOtp} disabled={loading} />
            </div>
            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="text-sm font-medium text-[#00553E] hover:underline disabled:opacity-60"
              >
                Renvoyer le code
              </button>
              <button
                type="button"
                onClick={handleVerify}
                disabled={loading || otp.length !== 6}
                className="h-10 px-4 rounded-[6px] font-semibold text-white disabled:opacity-60"
                style={{ background: "#00553E" }}
              >
                {loading ? "Vérification..." : "Valider"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
